/**
 * Le PDF des formulaires de /outils.
 *
 * Les documents sont composés en blocs par leur module voisin de `lib/tools`
 * (`{ titre, blocs: [...] }`), et ce fichier est le seul à savoir les poser sur
 * une page. L'aperçu à l'écran lit la même source, dans
 * components/Tools/DocumentPaper.jsx : sans cela, les deux divergent au premier
 * ajustement, et le lecteur télécharge autre chose que ce qu'il a relu.
 *
 * Quatre types de blocs, et rien d'autre tant qu'un document n'en réclame pas
 * un cinquième :
 *   - `paragraphe` : un texte au fil, coupé en bout de ligne.
 *   - `lignes` : des lignes courtes qui ne se recomposent pas, comme l'état
 *     civil d'une déclaration, où chacune tient son rang.
 *   - `signature` : un libellé, la place du paraphe, et le nom dessous quand le
 *     modèle le demande.
 *   - `note` : le renvoi de bas de page, plus petit.
 *   - `tableau` : des colonnes, un filet par ligne, un total en pied. Ajouté
 *     pour le tableau d'activité de l'INSEE, dont l'imprimé n'a pas de champs
 *     et se reproduit donc à l'identique. Options : `alignements` par colonne
 *     (« droite » pour des montants, qui doivent s'aligner sur leur unité) et
 *     `aplat` pour détacher la rangée de total.
 *   - `signatures` : deux pavés de signature CÔTE À CÔTE, chacun encadré. Les
 *     deux parties d'un devis se font face sur le papier ; empilés, ils
 *     coûtaient trente-quatre millimètres et une seconde page pour un
 *     document qui tenait sur une.
 *   - `intertitre` : une ligne en gras qui ouvre une partie. Ajouté pour la
 *     note de calcul, la seule pièce qui enchaîne plusieurs tableaux : il ne
 *     reste jamais seul en bas de page, et se colle au bloc qu'il annonce.
 *   - `entete` : deux colonnes côte à côte. Avec un `libelle`, elles portent
 *     leur intitulé en petites capitales grises : c'est ce qu'attend un
 *     document commercial, où l'émetteur et le destinataire doivent se
 *     distinguer d'un coup d'oeil. Sans `libelle`, elles rendent la
 *     disposition d'une lettre administrative française : expéditeur à gauche,
 *     destinataire à droite, lieu et date sous lui. Une chaîne vide dans
 *     `lignes` y saute une ligne.
 *
 * LES OPTIONS SONT TOUTES FACULTATIVES, et c'est délibéré. Une déclaration de
 * non-condamnation, une attestation, une mise en demeure sont des pièces de
 * dossier : elles doivent ressembler à une lettre, pas à un modèle. Le devis,
 * lui, part chez un client et représente l'entreprise. Ce sont les seuls
 * documents à demander de la forme, et ils sont les seuls à la demander.
 *
 * Hors de components/ et sans JSX, pour qu'un harnais puisse fabriquer un PDF
 * et le relire sans navigateur : c'est ainsi qu'on a vu que les accents
 * passaient.
 */

/* Millimètres, sur une page A4 de 210 sur 297. */
const MARGE = 25;
const LARGEUR = 210 - MARGE * 2;
const HAUT = 28;
const HAUTEUR_UTILE = 282;
/* L'écart avant un paraphe : le bloc de signature ouvre déjà par un blanc,
   et lui ajouter l'après-bloc ordinaire éloignait la signature de sa date. */
const AVANT_SIGNATURE = 3;
const INTERLIGNE = 5.6;
const INTERLIGNE_LIGNES = 6.6;
const APRES_BLOC = 7;
/* Ce qu'un bloc de signature occupe : le libellé s'il y en a un, la place du
   paraphe, puis le nom. Mesuré ici pour que le bloc PRÉCÉDENT puisse le
   réserver et qu'une signature ne se retrouve jamais seule en tête de page.
   La valeur est celle du PIRE cas, sans image : 22 mm de blanc puis le nom.
   Un paraphe tracé occupe moins, et composerPdf le sait, lui, puisqu'il tient
   l'image : la surestimer coûte cher, et pas là où on l'imagine. À 30 mm, une
   présentation d'activité qui dépassait d'un millimètre renvoyait sa date et
   son paraphe sur une seconde page autrement vide. */
const HAUTEUR_SIGNATURE = 28;
/* Le paraphe occupe 14 mm à partir de y+3, donc il finit à y+17. Le nom se
   posait à y+19 : un tracé qui descendait au bas du pavé traversait le nom, ce
   qu'un devis a montré tout de suite. Cinq millimètres de garde les séparent
   désormais, et la hauteur réservée suit. */
const APRES_PARAPHE = 22;
const HAUTEUR_SIGNATURE_TRACEE = 28;
/* La hauteur d'un pavé encadré : le libellé, l'écart, le cadre, la garde, puis
   le nom. Le nom se posait sur le trait du bas, faute de cette garde. */
const HAUTEUR_LIBELLE_CADRE = 3;
const HAUTEUR_BOITE_SIGNATURE = 20;
const GARDE_SOUS_LA_BOITE = 5.5;
const HAUTEUR_CADRE_SIGNATURE =
    HAUTEUR_LIBELLE_CADRE + HAUTEUR_BOITE_SIGNATURE + GARDE_SOUS_LA_BOITE + INTERLIGNE;
/* L'intitulé d'une colonne d'en-tête, en petites capitales grises. */
const INTERLIGNE_ENTETE = 5.4;
/* Ce qui sépare le titre du numéro de page, dans le pied. Exporté parce que le
   harnais doit retirer ce pied avant de comparer le PDF au document : il n'est
   dit nulle part dans les blocs, il est ajouté à la composition. */
export const PIED_SEPARATEUR = "\u00b7";
/* L'interligne dans une cellule de tableau, qui se compose plus serré que le
   corps du texte : la police y descend à 9,5 points. */
const INTERLIGNE_CELLULE = 4.6;

/** Ce qu'un bloc occupera en hauteur, pour décider d'un saut de page avant lui. */
function hauteurDe(pdf, bloc) {
    if (bloc.type === "lignes") return bloc.lignes.length * INTERLIGNE_LIGNES;
    if (bloc.type === "tableau") {
        const largeurs = bloc.largeurs || bloc.entetes.map(() => LARGEUR / bloc.entetes.length);
        const rangees = [bloc.entetes, ...bloc.lignes, ...(bloc.total ? [bloc.total] : [])];
        return rangees.reduce((total, cellules) => {
            const compte = Math.max(
                1,
                ...cellules.map((c) => pdf.splitTextToSize(String(c ?? ""), largeurs[0] - 4).length),
            );
            return total + Math.max(7, compte * INTERLIGNE_CELLULE + 2.4);
        }, 0);
    }
    if (bloc.type === "entete") {
        const lignes = Math.max(...bloc.colonnes.map((c) => c.lignes.length));
        const intitule = bloc.colonnes.some((c) => c.libelle) ? 5 : 0;
        return intitule + lignes * INTERLIGNE_ENTETE;
    }
    if (bloc.type === "signature") {
        return bloc.cadre ? HAUTEUR_CADRE_SIGNATURE : HAUTEUR_SIGNATURE;
    }
    if (bloc.type === "signatures") return HAUTEUR_CADRE_SIGNATURE;
    if (bloc.type === "intertitre") return pdf.splitTextToSize(bloc.texte, LARGEUR).length * 6;
    const interligne = bloc.type === "note" ? 5 : INTERLIGNE;
    return pdf.splitTextToSize(bloc.texte, LARGEUR).length * interligne;
}

/**
 * Compose le PDF et le rend, sans le remettre à personne.
 *
 * Séparé du téléchargement pour qu'un harnais puisse le relire : `save()` a
 * besoin d'un navigateur, `output()` non.
 *
 * `document` est renommé à l'entrée : gardé tel quel, il masquerait le document
 * du navigateur dans tout le module.
 *
 * jspdf est chargé à la demande : il pèse plus lourd que tout le reste de la
 * page, et neuf visiteurs sur dix lisent l'outil sans rien télécharger.
 */
export async function composerPdf({ document: doc, signature }) {
    if (!doc) return null;
    const { jsPDF } = await import("jspdf");
    const pdf = new jsPDF({ unit: "mm", format: "a4" });
    let y = HAUT;
    const hauteurDuParaphe = signature ? HAUTEUR_SIGNATURE_TRACEE : HAUTEUR_SIGNATURE;

    /* Une page suffit à tous les documents connus, mais un nom très long peut
       faire déborder : mieux vaut une seconde page qu'un texte tronqué. */
    const place = (hauteur) => {
        if (y + hauteur > HAUTEUR_UTILE) {
            pdf.addPage();
            y = HAUT;
        }
    };

    const ecrire = (texte, interligne) => {
        for (const ligne of pdf.splitTextToSize(texte, LARGEUR)) {
            place(interligne);
            pdf.text(ligne, MARGE, y);
            y += interligne;
        }
    };

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(13);
    ecrire(doc.titre, 6.5);
    y += APRES_BLOC;

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(11);

    for (const [rang, bloc] of doc.blocs.entries()) {
        /* Une signature orpheline en tête de page fait douteux sur une lettre
           officielle. Le bloc qui la précède réserve donc sa hauteur : s'ils ne
           tiennent pas ensemble, ils passent ensemble à la page suivante. */
        const suivant = doc.blocs[rang + 1];
        /* La réserve compte AUSSI le blanc qui les sépare : sans lui, le bloc
           précédent s'autorisait à rester quand la signature, elle, ne tenait
           plus, et les deux se retrouvaient séparés d'une page.

           Elle compte enfin TOUTE LA SUITE de pavés de signature, pas seulement
           le premier : un devis en porte deux, celui de l'émetteur et celui du
           client, et n'en réserver qu'un renvoyait le second seul sur une page
           autrement vide. */
        let paves = 0;
        let reserve = 0;
        while (["signature", "signatures"].includes(doc.blocs[rang + 1 + paves]?.type)) {
            const pave = doc.blocs[rang + 1 + paves];
            // Chaque pavé réserve SA hauteur : un pavé encadré en occupe plus
            // qu'un paraphe posé au fil du texte.
            reserve +=
                (pave.type === "signatures" || pave.cadre
                    ? HAUTEUR_CADRE_SIGNATURE
                    : hauteurDuParaphe) + AVANT_SIGNATURE;
            paves++;
        }
        if (reserve) place(hauteurDe(pdf, bloc) + reserve);

        if (bloc.type === "lignes") {
            for (const ligne of bloc.lignes) {
                place(INTERLIGNE_LIGNES);
                pdf.text(ligne, MARGE, y);
                y += INTERLIGNE_LIGNES;
            }
        } else if (bloc.type === "entete") {
            /* Les colonnes ne s'écrivent pas l'une après l'autre : elles
               partent du MÊME y, et c'est la plus haute qui décide de la
               suite. */
            place(hauteurDe(pdf, bloc));
            const gouttiere = 10;
            const colonne = (LARGEUR - gouttiere) / 2;
            const depart = y;
            let bas = y;

            bloc.colonnes.forEach((col, i) => {
                const x = MARGE + i * (colonne + gouttiere);
                let ligne = depart;

                if (col.libelle) {
                    pdf.setFont("helvetica", "bold");
                    pdf.setFontSize(7.5);
                    pdf.setTextColor(120);
                    pdf.text(col.libelle.toUpperCase(), x, ligne);
                    pdf.setTextColor(0);
                    ligne += 5;
                }

                pdf.setFont("helvetica", "normal");
                pdf.setFontSize(9.5);
                for (const texte of col.lignes) {
                    // Une chaîne vide saute une ligne : c'est ainsi qu'une
                    // lettre détache le lieu et la date du destinataire.
                    if (!texte) {
                        ligne += INTERLIGNE_ENTETE;
                        continue;
                    }
                    // Une adresse un peu longue se coupe dans SA colonne, elle
                    // ne déborde pas sur celle d'à côté.
                    for (const morceau of pdf.splitTextToSize(texte, colonne)) {
                        pdf.text(morceau, x, ligne);
                        ligne += INTERLIGNE_ENTETE;
                    }
                }
                bas = Math.max(bas, ligne);
            });

            pdf.setFontSize(11);
            /* L'en-tête se compose en 9,5 points, le corps en 11 : sans ce
               supplément, l'objet d'une lettre venait se coller sous la
               dernière ligne du destinataire. */
            y = bas - INTERLIGNE_ENTETE + 5;
        } else if (bloc.type === "signature") {
            place(bloc.cadre ? HAUTEUR_CADRE_SIGNATURE : hauteurDuParaphe);
            // Le libellé est facultatif : certains documents n'annoncent pas
            // leur signature, ils la laissent venir.
            if (bloc.libelle) {
                if (bloc.cadre) {
                    /* En gris et plus petit, mais PAS en capitales : le libellé
                       d'un pavé de signature est une date ou une consigne, pas
                       une étiquette de colonne. « RENNES, LE 8 SEPTEMBRE 2026 »
                       criait, et disait autre chose que le document. */
                    pdf.setFontSize(8.5);
                    pdf.setTextColor(120);
                    pdf.text(bloc.libelle, MARGE, y);
                    pdf.setTextColor(0);
                    pdf.setFontSize(11);
                    y += HAUTEUR_LIBELLE_CADRE;
                } else {
                    pdf.text(bloc.libelle, MARGE, y);
                }
            }
            /* `cadre` trace le rectangle où l'on signe. Sur un devis, le pavé
               du client est ce que le document DEMANDE : sans trait, personne
               ne sait où porter la mention, et rien ne le repère sur une page
               imprimée. */
            if (bloc.cadre) {
                pdf.setDrawColor(180);
                pdf.roundedRect(MARGE, y, LARGEUR, HAUTEUR_BOITE_SIGNATURE, 1.5, 1.5);
            }
            // `paraphe: false` réserve la place SANS y poser le tracé. Un devis
            // porte deux pavés, celui de l'émetteur et celui du client : y
            // stamper le même paraphe reviendrait à remettre au client un
            // document déjà signé en son nom.
            if (signature && bloc.paraphe !== false) {
                // 55 mm de large, la hauteur suit le rapport du pavé.
                pdf.addImage(signature, "PNG", MARGE + (bloc.cadre ? 3 : 0), y + 3, 55, 14);
                y += bloc.cadre ? HAUTEUR_BOITE_SIGNATURE + GARDE_SOUS_LA_BOITE : APRES_PARAPHE;
            } else {
                /* Encadré, le nom se pose SOUS le trait du bas, jamais dessus :
                   il chevauchait le cadre, et « Gaëlle PERRIN » était barré par
                   la bordure de son propre pavé. */
                y += bloc.cadre ? HAUTEUR_BOITE_SIGNATURE + GARDE_SOUS_LA_BOITE : 25;
            }
            if (bloc.nom) {
                if (bloc.cadre) {
                    pdf.setFontSize(9.5);
                    pdf.setTextColor(120);
                }
                pdf.text(bloc.nom, MARGE, y);
                pdf.setTextColor(0);
                pdf.setFontSize(11);
                y += INTERLIGNE;
            }
        } else if (bloc.type === "signatures") {
            /* Les deux parties se font face. Elles partent du même y, et la
               plus haute décide de la suite, comme pour l'en-tête. */
            place(HAUTEUR_CADRE_SIGNATURE);
            const gouttiere = 10;
            const colonne = (LARGEUR - gouttiere) / 2;
            const depart = y;
            let bas = y;

            bloc.colonnes.forEach((pave, i) => {
                const x = MARGE + i * (colonne + gouttiere);
                let ligne = depart;

                if (pave.libelle) {
                    pdf.setFontSize(8.5);
                    pdf.setTextColor(120);
                    for (const morceau of pdf.splitTextToSize(pave.libelle, colonne)) {
                        pdf.text(morceau, x, ligne);
                        ligne += 4;
                    }
                    pdf.setTextColor(0);
                    pdf.setFontSize(11);
                }
                const hautDeLaBoite = ligne - 1;
                pdf.setDrawColor(180);
                pdf.roundedRect(x, hautDeLaBoite, colonne, HAUTEUR_BOITE_SIGNATURE, 1.5, 1.5);
                if (signature && pave.paraphe !== false) {
                    // Le paraphe tient dans SA colonne : 45 mm plutôt que 55.
                    pdf.addImage(signature, "PNG", x + 3, hautDeLaBoite + 3, 45, 11.5);
                }
                ligne = hautDeLaBoite + HAUTEUR_BOITE_SIGNATURE + GARDE_SOUS_LA_BOITE;
                if (pave.nom) {
                    pdf.setFontSize(9.5);
                    pdf.setTextColor(120);
                    pdf.text(pave.nom, x, ligne);
                    pdf.setTextColor(0);
                    pdf.setFontSize(11);
                    ligne += INTERLIGNE;
                }
                bas = Math.max(bas, ligne);
            });

            y = bas - INTERLIGNE + 2;
        } else if (bloc.type === "tableau") {
            const largeurs = bloc.largeurs || bloc.entetes.map(() => LARGEUR / bloc.entetes.length);
            const filet = MARGE + largeurs.reduce((a, b) => a + b, 0);

            /* Une cellule trop large ne se tronque pas : elle se coupe en
               lignes, et c'est la rangée qui grandit. Ne poser que la première
               ligne avait fait disparaître le « A. » de « Pourcentage du
               C. A. » sans que rien ne le signale, et une désignation un peu
               longue y aurait perdu sa fin. */
            /* Une colonne de montants s'aligne À DROITE, sinon les unités ne
               se superposent pas et « 500,00 € » et « 1,00 € » commencent au
               même endroit : l'oeil ne peut plus comparer deux prix. Les
               tableaux qui ne demandent rien restent à gauche. */
            const alignements = bloc.alignements || bloc.entetes.map(() => "gauche");

            const rangee = (cellules, gras, aplat) => {
                pdf.setFont("helvetica", gras ? "bold" : "normal");
                const colonnes = cellules.map((cellule, i) =>
                    pdf.splitTextToSize(String(cellule ?? ""), largeurs[i] - 4),
                );
                const compte = Math.max(1, ...colonnes.map((c) => c.length));
                const hauteurLigne = Math.max(7, compte * INTERLIGNE_CELLULE + 2.4);
                place(hauteurLigne);
                if (aplat) {
                    pdf.setFillColor(244);
                    pdf.rect(MARGE, y, filet - MARGE, hauteurLigne, "F");
                }
                let x = MARGE;
                colonnes.forEach((lignesCellule, i) => {
                    const droite = alignements[i] === "droite";
                    lignesCellule.forEach((texte, rangDeLigne) => {
                        pdf.text(
                            texte,
                            droite ? x + largeurs[i] - 2 : x + 2,
                            y + 4.8 + rangDeLigne * INTERLIGNE_CELLULE,
                            droite ? { align: "right" } : undefined,
                        );
                    });
                    x += largeurs[i];
                });
                y += hauteurLigne;
                pdf.setDrawColor(180);
                pdf.line(MARGE, y, filet, y);
                pdf.setFont("helvetica", "normal");
            };

            pdf.setFontSize(9.5);
            pdf.setDrawColor(180);
            pdf.line(MARGE, y, filet, y);
            rangee(bloc.entetes, true);
            for (const ligne of bloc.lignes) rangee(ligne, false);
            if (bloc.total) rangee(bloc.total, true, bloc.aplat);
            pdf.setFontSize(11);
            // Un tableau se détache mieux du texte qui le suit : sans ce
            // supplément, son dernier filet touche presque la ligne suivante
            // alors qu'un blanc plus large le précède.
            y += 4;
        } else if (bloc.type === "intertitre") {
            /* Un intertitre seul en bas de page annonce une partie qu'on ne
               lit qu'en tournant la page : il réserve de quoi poser au moins
               la première rangée de ce qu'il annonce. */
            place(hauteurDe(pdf, bloc) + 16);
            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(11.5);
            ecrire(bloc.texte, 6);
            pdf.setFont("helvetica", "normal");
            pdf.setFontSize(11);
        } else if (bloc.type === "note") {
            pdf.setFontSize(9);
            ecrire(bloc.texte, 5);
            pdf.setFontSize(11);
        } else {
            ecrire(bloc.texte, INTERLIGNE);
        }
        y += suivant?.type === "signature" ? AVANT_SIGNATURE : bloc.type === "intertitre" ? 1.5 : APRES_BLOC;
    }

    /* UN DOCUMENT QUI TIENT SUR PLUSIEURS PAGES DOIT LE DIRE. Le devis se signe
       sur sa dernière page : c'est celle que le client renvoie, souvent seule,
       et sans rien dessus rien ne dit de quel devis elle vient ni s'il en
       manque une. Un document d'une seule page n'a pas ce problème et ne porte
       donc pas de pied. */
    const pages = pdf.getNumberOfPages();
    if (pages > 1) {
        for (let n = 1; n <= pages; n++) {
            pdf.setPage(n);
            pdf.setFont("helvetica", "normal");
            pdf.setFontSize(8);
            pdf.setTextColor(140);
            pdf.text(`${doc.titre} ${PIED_SEPARATEUR} ${n}/${pages}`, 105, 288, { align: "center" });
            pdf.setTextColor(0);
        }
    }

    return pdf;
}

/** Fabrique le PDF et le remet au navigateur. */
export async function telechargerDocument({ document: doc, signature, nomFichier }) {
    const pdf = await composerPdf({ document: doc, signature });
    if (pdf) pdf.save(nomFichier);
}
